import {
  AccountAmountField,
  AccountAmountValue,
  Box,
  BrightyStepButton,
  Button,
  ButtonLink,
  ContentIsland,
  FormContentIsland,
  Grid,
  Stack,
  Step,
  Stepper,
  TransferBreakdown,
  UiFee,
  VaultListItemContent,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../Layout/MainLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccountsWithAccountQuery } from '../../../api/accounts';
import { ApiAccount, ApiVault } from '../../../openapi';
import {
  mapClientMoneyToServerMoney,
  parseFloatValue,
  serverAmountToClientAmount,
} from '@razz-team/utils';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { TransferComplete } from '../../TransferComplete/TransferComplete';
import { Form } from '@razz-team/ui-web/src/components/Form/Form';
import { Route } from '../../../routes/account.vaults_.topup';
import {
  useVaultDepositIntentMutation,
  useVaultDepositMutation,
  useVaultsQuery,
} from '../../../api/vaults';
import { detectFromTopUpAccount, detectTransferVault } from './utils';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';

type Directions = {
  from: AccountAmountValue<ApiAccount> | undefined;
  to: AccountAmountValue<ApiVault> | undefined;
};

export function VaultDepositPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const vaultsQuery = useVaultsQuery();
  const accountsQuery = useAccountsWithAccountQuery(search.accountId);

  const intentMutation = useVaultDepositIntentMutation();
  const { mutate: mutateIntent, data: intent } = intentMutation;
  const idempotencyKey = useIdempotencyKey();
  const depositMutation = useVaultDepositMutation({ idempotencyKey });

  const [directions, setDirections] = useState<Directions>(() => ({
    from: undefined,
    to: undefined,
  }));

  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);
  const vaults = useMemo(() => vaultsQuery.data ?? [], [vaultsQuery.data]);

  useEffect(() => {
    setDirections((prev): Directions => {
      if (prev.from && prev.to) return prev;
      const from = detectFromTopUpAccount({
        accounts,
        defaultAccountId: search.accountId,
      });
      const to = detectTransferVault({
        vaults,
        defaultVaultId: search.vaultId,
      });
      return {
        from: from ? { clientAmount: '', account: from } : undefined,
        to: to ? { clientAmount: '', account: to } : undefined,
      };
    });
  }, [search.vaultId, search.accountId, accounts, vaults]);

  const handleChangeFrom = useCallback(
    (nextFrom: AccountAmountValue<ApiAccount>) => {
      setDirections(({ to }) => {
        if (to) {
          const amount = mapClientMoneyToServerMoney({
            amount: parseFloatValue(nextFrom.clientAmount),
            currency: nextFrom.account.balance.currency,
          });
          mutateIntent({
            side: 'SELL',
            amount,
            sourceCurrency: amount.currency,
            targetCurrency: to.account.balance.currency,
            sourceAccountId: nextFrom.account.id,
            targetVaultId: to.account.id,
          });
        }

        return { to, from: nextFrom };
      });
    },
    [mutateIntent],
  );

  const handleChangeTo = useCallback(
    (nextTo: AccountAmountValue<ApiVault>) => {
      setDirections(({ from }) => {
        if (from) {
          const amount = mapClientMoneyToServerMoney({
            amount: parseFloatValue(nextTo.clientAmount),
            currency: nextTo.account.balance.currency,
          });
          mutateIntent({
            side: 'BUY',
            amount,
            sourceCurrency: from.account.balance.currency,
            targetCurrency: amount.currency,
            sourceAccountId: from.account.id,
            targetVaultId: nextTo.account.id,
          });
        }

        return { from, to: nextTo };
      });
    },
    [mutateIntent],
  );

  useEffect(() => {
    if (!intent) return;
    if (intent.quote.fx) {
      const {
        amount,
        quote: { targetAmount },
      } = intent;
      const fx = intent.quote.fx;
      setDirections((prev) => {
        if (fx.side === 'SELL' && prev.from && prev.to) {
          return {
            from: prev.from,
            to: {
              account: prev.to.account,
              clientAmount: String(serverAmountToClientAmount(targetAmount)),
            },
          };
        }

        if (fx.side === 'BUY' && prev.from && prev.to) {
          return {
            to: prev.to,
            from: {
              account: prev.from.account,
              clientAmount: String(serverAmountToClientAmount(amount)),
            },
          };
        }

        return prev;
      });
    } else {
      // support sending between accounts with the same currency
      setDirections((prev) => {
        const { to, from } = prev;
        if (
          to &&
          from &&
          to.account.balance.currency === from.account.balance.currency &&
          to.account.balance.currency === intent.quote.targetAmount.currency &&
          from.account.balance.currency === intent.quote.sourceAmount.currency
        ) {
          return {
            from: {
              account: from.account,
              clientAmount: String(serverAmountToClientAmount(intent.quote.sourceAmount)),
            },
            to: {
              account: to.account,
              clientAmount: String(serverAmountToClientAmount(intent.quote.targetAmount)),
            },
          };
        }

        return prev;
      });
    }
  }, [intent]);

  const [step, setStep] = useState<'amount' | 'confirm'>('amount');
  const backToEdit = useCallback(() => setStep('amount'), []);

  const handleSubmit = useCallback(() => {
    if (step === 'amount') {
      setStep('confirm');
    } else if (depositMutation.isIdle && intentMutation.data && directions.from && directions.to) {
      depositMutation.mutate({
        hash: intentMutation.data.hash,
        fees: intentMutation.data.fees,
        quote: intentMutation.data.quote,
        sourceAccountId: directions.from?.account.id,
        targetVaultId: directions.to?.account.id,
      });
    }
  }, [step, directions, intentMutation.data, depositMutation]);

  useEffect(() => {
    if (depositMutation.isSuccess) {
      setStep('confirm');
    }
  }, [depositMutation.isSuccess]);

  const uiFees = useMemo(() => {
    return intentMutation.data?.fees
      ? Object.entries(intentMutation.data?.fees ?? {}).reduce((acc, [type, fee]) => {
          if (fee?.sourceAmount) {
            acc.push({
              type,
              value: fee.sourceAmount,
            });
          }
          return acc;
        }, [] as UiFee[])
      : null;
  }, [intentMutation.data?.fees]);

  const uiRate = useMemo(() => {
    if (intentMutation.data) {
      const { fx } = intentMutation.data.quote;
      if (fx) {
        return {
          price: fx.side === 'BUY' ? fx.rate.ask : fx.rate.bid,
          currencyPair: fx.rate.currencyPair,
        };
      }

      return undefined;
    }

    return null;
  }, [intentMutation.data]);

  const fromTitle = useMemo(() => {
    const account = directions.from?.account;
    if (!account) return t('From');

    const accountName =
      account.name ?? t('Account {{currency}}', { currency: account.balance.currency });
    return t('From {{accountName}}', { accountName });
  }, [directions.from?.account, t]);

  const toTitle = useMemo(() => {
    const account = directions.to?.account;
    if (!account) return t('To');

    const accountName = t('Vault {{currency}}', { currency: account.balance.currency });
    return t('To {{accountName}}', { accountName });
  }, [directions.to?.account, t]);

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Top up')}
          parent={{
            to: '/account/vaults',
            title: t('Earning vaults'),
          }}
        />
      }
      compact
    >
      <Grid size={12}>
        <Box sx={{ width: '100%' }}>
          <Stepper alternativeLabel activeStep={step === 'confirm' ? 1 : 0}>
            <Step active={step === 'amount'}>
              <BrightyStepButton onClick={backToEdit}>{t('Amount')}</BrightyStepButton>
            </Step>
            <Step active={step === 'confirm'}>
              <BrightyStepButton>{t('Confirm')}</BrightyStepButton>
            </Step>
          </Stepper>
        </Box>
      </Grid>
      <Grid
        component={Form}
        container
        display="flex"
        justifyContent="center"
        onSubmit={handleSubmit}
        size={12}
      >
        <Stack direction="column" width="100%" maxWidth={500} spacing={6} alignItems="stretch">
          {depositMutation.data?.transaction ? (
            <TransferComplete transaction={depositMutation.data?.transaction} />
          ) : (
            <>
              {step === 'amount' ? (
                <FormContentIsland title={fromTitle}>
                  <AccountAmountField
                    value={directions.from}
                    options={accounts}
                    onChange={handleChangeFrom}
                    autoFocus
                    helperSellAll
                    sellAllWord={t('Deposit')}
                    validateBalance
                  />
                </FormContentIsland>
              ) : null}

              <ContentIsland
                title={step === 'confirm' ? t('Deposit details') : undefined}
                actions={
                  step === 'confirm' ? (
                    <Button variant="text" color="info" onClick={backToEdit}>
                      {t('Edit')}
                    </Button>
                  ) : undefined
                }
              >
                <TransferBreakdown
                  variant={step === 'confirm' ? 'confirm' : 'form'}
                  side={intentMutation.data?.quote.fx?.side}
                  amountToDebit={step === 'confirm' ? intentMutation.data?.amount : undefined}
                  amountToConvert={intentMutation.data?.quote.sourceAmount ?? null}
                  fees={uiFees}
                  rate={uiRate}
                  amountToTransfer={
                    step === 'confirm' ? intentMutation.data?.quote.targetAmount : undefined
                  }
                  error={depositMutation.error ?? intentMutation.error}
                />
              </ContentIsland>
              {step === 'amount' ? (
                <FormContentIsland title={toTitle}>
                  <AccountAmountField<ApiVault>
                    value={directions.to}
                    options={vaults}
                    onChange={handleChangeTo}
                    renderItemContent={(vault) => <VaultListItemContent vault={vault} />}
                  />
                </FormContentIsland>
              ) : null}
            </>
          )}
          {depositMutation.data?.transaction ? (
            <ButtonLink
              size="large"
              color="primary"
              to="/account/vaults/$vaultId"
              params={{ vaultId: depositMutation.variables.targetVaultId }}
              fullWidth
            >
              {t('Done')}
            </ButtonLink>
          ) : (
            <Button
              type="submit"
              size="large"
              color="primary"
              loading={intentMutation.isPending || depositMutation.isPending}
              disabled={!intentMutation.isSuccess}
              fullWidth
            >
              {step === 'confirm' ? t('Confirm') : t('Review order')}
            </Button>
          )}
        </Stack>
      </Grid>
    </MainLayout>
  );
}
